import React, { useEffect, useState, ChangeEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IGetOrderDTO from "../../../models/DTO/IGetOrderDTO";
import IOnGetOrderDTO from "../../../models/DTO/IOnGetOrderDTO";
import IUpdateOrderEntryDetailDTO from "../../../models/DTO/IUpdateOrderEntryDetailDTO";
import IOnUpdateOrderEntryDetailDTO from "../../../models/DTO/IOnUpdateOrderEntryDetailDTO";
import IOrder from "../../../models/IOrder";
import ActivityIndicator from "../../shared/ActivityIndicator/ActivityIndicator";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwTitle from "../../shared/OwTitle/OwTitle";
import UserService from "../../../services/UserService";
import { isDeliveryDeadlinesEnabled } from "../../business/BusinessSettings/DeliveryDeadlines/deliveryDeadlineUtils";
import AddressDisplay from "../../shared/AddressDisplay/AddressDisplay";
import { EcommercePaymentSummary, getSummarizedPayments } from "../EcommercePaymentSummary/EcommercePaymentSummary";
import OwButton from "../../shared/OwButton/OwButton";
import IImageSizeEnum from "../../../models/IImageSizeEnum";
import OwS3ImageViewer from "../../shared/OwS3ImageViewer/OwS3ImageViewer";
import { QtyDetail } from "../../shared/QtyDetail/QtyDetail";
import { FilesDownloadSection } from "../../shared/FilesDownloadSection/FilesDownloadSection";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import { KitItemsDisplay } from "../../shared/KitItemsDisplay/KitItemsDisplay";
import HelperService from "../../../services/HelperService";
import { useAppContextNonNull } from "@contexts/AppContext";
import { OwFnInput } from "../../shared/OwFnInput/OwFnInput";
import OwTextArea from "../../shared/OwTextArea/OwTextArea";
import OwEditModeToggle from "../../shared/OwEditModeToggle/OwEditModeToggle";
import { OrdersService } from "@components/orders/Orders/OrdersService";
import SupplierService from "@services/SupplierService";
import "./EcommerceOrderDetails.scss";

type Props = {};

export const EcommerceOrderDetails: React.FC<Props> = (props) => {
    const { userModel, supplierModel, vendorModel, socket, uiContext } = useAppContextNonNull();

    const hidePricing = UserService.hidePricing(supplierModel, userModel, vendorModel);

    const params = useParams();
    const history = useNavigate();

    const { guid } = params;

    const [order, setOrder] = useState<IOrder>();

    const [allowEditOrderDetails, setAllowEditOrderDetails] = useState(false);

    // Edit mode states
    const [poNoEditMode, setPoNoEditMode] = useState(false);
    const [noteEditMode, setNoteEditMode] = useState(false);
    const [editedPoNo, setEditedPoNo] = useState("");
    const [editedNote, setEditedNote] = useState("");

    const onGetOrder = (data: IOnGetOrderDTO) => {
        setOrder(data.order);
        setEditedPoNo(data.order.po_no || "");
        setEditedNote(data.order.note || "");

        // Editable only when the supplier allows eCommerce order edits AND the workflow hasn't made the order read-only
        // for this user. "Is Customer Read Only" carries forward to every later step, which isReadOnly accounts for.
        const readOnly = OrdersService.isReadOnly({
            order: data.order,
            isOfflineOrder: false,
            hasEditMode: true,
            userModel,
            supplierModel,
            section: "DETAILS_PANEL",
        });

        setAllowEditOrderDetails(!!supplierModel.ecomm_edit_order_details && !readOnly);
    };

    const onUpdateOrderEntryDetail = (data: IOnUpdateOrderEntryDetailDTO) => {
        setEditedPoNo(data.order_detail.po_no || "");
        setEditedNote(data.order_detail.note || "");
        setPoNoEditMode(false);
        setNoteEditMode(false);

        getOrder();
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.OrderOnGetOrder, fn: onGetOrder },
        { endpoint: EndpointsEnum.OrderEntryOnUpdateOrderEntryDetail, fn: onUpdateOrderEntryDetail },
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        getOrder();

        return () => {
            for (const _socket of sockets) {
                socket.removeListener(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    const getOrder = () => {
        if (guid) {
            const payload: IGetOrderDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                vendorGuid: vendorModel.guid,
                orderGuid: guid,
            };

            socket.emit(EndpointsEnum.OrderGetOrder, payload);
        }
    };

    const viewInvoice = () => {
        history(`/ecomm/orders/invoice/${guid}`);
    };

    // PO Number edit handlers
    const togglePoNoEditMode = () => {
        setPoNoEditMode(true);
        setEditedPoNo(order?.po_no || "");
    };

    const cancelPoNoEdit = () => {
        setPoNoEditMode(false);
        setEditedPoNo(order?.po_no || "");
    };

    const savePoNo = () => {
        if (order) {
            const payload: IUpdateOrderEntryDetailDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                vendorGuid: vendorModel.guid,
                orderGuid: order.guid,
                po_no: editedPoNo,
            };
            socket.emit(EndpointsEnum.OrderEntryUpdateOrderEntryDetail, payload);
        }
    };

    const handlePoNoChange = (e: ChangeEvent<HTMLInputElement>) => {
        setEditedPoNo(e.target.value);
    };

    // Note edit handlers
    const toggleNoteEditMode = () => {
        setNoteEditMode(true);
        setEditedNote(order?.note || "");
    };

    const cancelNoteEdit = () => {
        setNoteEditMode(false);
        setEditedNote(order?.note || "");
    };

    const saveNote = () => {
        if (order) {
            const payload: IUpdateOrderEntryDetailDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                vendorGuid: vendorModel.guid,
                orderGuid: order.guid,
                note: editedNote,
            };
            socket.emit(EndpointsEnum.OrderEntryUpdateOrderEntryDetail, payload);
        }
    };

    const handleNoteChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        setEditedNote(e.target.value);
    };

    const formatMoney = (value?: number | null) =>
        HelperService.formatCurrency({
            value: value || 0,
            currency: "USD",
            precision: supplierModel.pricing_precision || 2,
        });

    const hasPayments = !!order && getSummarizedPayments(order.Payments).length > 0;
    const showReference = !!order && (allowEditOrderDetails || !!order.po_no || !!order.note);

    return (
        <div className={"EcommerceOrderDetails"}>
            <div className={"order-details-page"}>
                <Link to={"/ecomm/orders"} className={"order-details-back"}>
                    <i className={"fas fa-arrow-left"} />
                    <OwLabel title={"back_to_orders"} noBottomMargin={true} />
                </Link>

                {!order ? (
                    <div className={"order-details-card order-details-loading"}>
                        <ActivityIndicator active={true} />
                        <OwLabel title={"loading"} noBottomMargin={true} />
                    </div>
                ) : (
                    <>
                        <div className={"order-details-header"}>
                            <div>
                                <OwTitle title={"order_details"} tag={"h3"} className={"order-details-title"} />
                                <div className={"order-details-meta"}>
                                    <span className={"order-details-meta-item"}>
                                        <OwLabel title={"order_no"} noBottomMargin={true} className={"order-details-meta-label"} />
                                        <strong>{order.name}</strong>
                                    </span>
                                    <span className={"order-details-meta-item"}>
                                        <OwLabel title={"ordered_on"} noBottomMargin={true} className={"order-details-meta-label"} />
                                        <span>{UserService.parseDate(order.created_at)}</span>
                                    </span>
                                    {isDeliveryDeadlinesEnabled(order.Vendor) && order.delivery_date ? (
                                        <span className={"order-details-meta-item"}>
                                            <OwLabel title={"delivery_date"} noBottomMargin={true} className={"order-details-meta-label"} />
                                            <span>{UserService.parseUTCDate(order.delivery_date)}</span>
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                            <OwButton
                                title={"view_or_print_invoice"}
                                className={"order-details-btn-primary"}
                                leftIconClass={"fas fa-print"}
                                fn={viewInvoice}
                            />
                        </div>

                        <div className={"order-details-card order-details-overview"}>
                            <div className={"order-details-section"}>
                                <OwLabel title={"shipping_address"} className={"order-details-section-label"} />
                                <AddressDisplay address={order.Ship_To} showName={true} showCompanyName={true} />
                            </div>

                            {hasPayments ? (
                                <div className={"order-details-section"}>
                                    <OwLabel title={"payment"} className={"order-details-section-label"} />
                                    <EcommercePaymentSummary
                                        payments={order.Payments}
                                        hidePricing={hidePricing}
                                        pricingPrecision={supplierModel.pricing_precision}
                                    />
                                </div>
                            ) : null}

                            {!hidePricing ? (
                                <div className={"order-details-section order-details-totals"}>
                                    <OwLabel title={"order_summary"} className={"order-details-section-label"} />
                                    <div className={"order-details-total-row"}>
                                        <OwLabel title={"subtotal"} noBottomMargin={true} />
                                        <span>{formatMoney(order.sub_total)}</span>
                                    </div>
                                    {order.discount_total && order.discount_total > 0 ? (
                                        <div className={"order-details-total-row"}>
                                            <OwLabel title={"discount"} noBottomMargin={true} />
                                            <span>-{formatMoney(order.discount_total)}</span>
                                        </div>
                                    ) : null}
                                    {order.promo_code && order.promo_discount_total && order.promo_discount_total > 0 ? (
                                        <div className={"order-details-total-row"} data-testid={"order-details-promo"}>
                                            <span>
                                                <OwLabel title={"promo_code"} noBottomMargin={true} /> ({order.promo_code})
                                            </span>
                                            <span>-{formatMoney(order.promo_discount_total)}</span>
                                        </div>
                                    ) : null}
                                    <div className={"order-details-total-row"}>
                                        <OwLabel title={"tax"} noBottomMargin={true} />
                                        <span>{formatMoney(order.tax)}</span>
                                    </div>
                                    <div className={"order-details-total-row"}>
                                        <OwLabel title={"shipping"} noBottomMargin={true} />
                                        <span>{formatMoney(order.shipping)}</span>
                                    </div>
                                    <div className={"order-details-total-row is-grand-total"}>
                                        <OwLabel title={"grand_total"} noBottomMargin={true} />
                                        <span>{formatMoney(order.grand_total)}</span>
                                    </div>
                                </div>
                            ) : null}
                        </div>

                        {showReference ? (
                            <div className={"order-details-card order-details-reference"}>
                                <div className={"order-details-section"}>
                                    <div className={"order-details-section-heading"}>
                                        <OwLabel title={"po_no"} className={"order-details-section-label"} noBottomMargin={true} />
                                        {allowEditOrderDetails && (
                                            <OwEditModeToggle
                                                editMode={poNoEditMode}
                                                cancelEditFn={cancelPoNoEdit}
                                                saveEditFn={savePoNo}
                                                toggleEditFn={togglePoNoEditMode}
                                            />
                                        )}
                                    </div>
                                    {poNoEditMode ? (
                                        <OwFnInput
                                            name="po_no"
                                            value={editedPoNo}
                                            fn={handlePoNoChange}
                                            placeholder="po_no"
                                            className="w-100"
                                        />
                                    ) : (
                                        <div className={order.po_no ? "" : "order-details-empty"}>{order.po_no || "—"}</div>
                                    )}
                                </div>

                                <div className={"order-details-section order-details-note"}>
                                    <div className={"order-details-section-heading"}>
                                        <OwLabel title={"note"} className={"order-details-section-label"} noBottomMargin={true} />
                                        {allowEditOrderDetails && (
                                            <OwEditModeToggle
                                                editMode={noteEditMode}
                                                cancelEditFn={cancelNoteEdit}
                                                saveEditFn={saveNote}
                                                toggleEditFn={toggleNoteEditMode}
                                            />
                                        )}
                                    </div>
                                    {noteEditMode ? (
                                        <OwTextArea
                                            name="note"
                                            value={editedNote}
                                            fn={handleNoteChange}
                                            placeholder="note"
                                            rows={3}
                                            className="w-100"
                                        />
                                    ) : (
                                        <div className={order.note ? "order-details-note-text" : "order-details-empty"}>
                                            {order.note || "—"}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : null}

                        <div className={"order-details-card order-details-items"}>
                            <div className={"order-details-items-header"}>
                                <OwLabel title={"items"} className={"order-details-section-label"} noBottomMargin={true} />
                                <span className={"order-details-count"}>{order.Order_Items.length}</span>
                            </div>
                            <ul className={"order-details-item-list"}>
                                {order.Order_Items.map((orderItem) => (
                                    <li className={"order-details-item"} key={orderItem.guid}>
                                        <div className={"order-details-item-image"}>
                                            <OwS3ImageViewer
                                                catalogItem={orderItem.Catalog_Item}
                                                isMicroView={false}
                                                attachmentIndex={0}
                                                imageIndex={IImageSizeEnum.THUMB}
                                                className={"w-5r h-5r"}
                                                includeAllImages={false}
                                                hideEmptyImage={false}
                                            />
                                        </div>
                                        <div className={"order-details-item-info"}>
                                            <div className={"order-details-item-name"}>{orderItem.Catalog_Item?.name}</div>
                                            {orderItem.Catalog_Item?.sku ? (
                                                <div className={"order-details-item-sku"}>
                                                    <OwLabel title={"sku"} noBottomMargin={true} /> {orderItem.Catalog_Item.sku}
                                                </div>
                                            ) : null}
                                            <div className={"order-details-item-qty"}>
                                                <QtyDetail item={orderItem} />
                                                {!hidePricing ? (
                                                    <>
                                                        <OwLabel title={"at"} noBottomMargin={true} />
                                                        <span>{formatMoney(orderItem.cost_price_frozen)}</span>
                                                    </>
                                                ) : null}
                                            </div>
                                            {orderItem.Catalog_Item ? (
                                                <FilesDownloadSection catalogItem={orderItem.Catalog_Item} readOnly={true} />
                                            ) : null}
                                            <KitItemsDisplay orderItem={orderItem} />
                                        </div>
                                        {!hidePricing ? (
                                            <div className={"order-details-item-total"}>{formatMoney(orderItem.total)}</div>
                                        ) : null}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
